import { useEffect } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Radio, Row, TimePicker } from 'antd';
import type { FormInstance } from 'antd/es/form';
import moment, { type Moment } from 'moment';

import { SHIFT_TYPES } from '@src/packages/shifts/constants';
import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';

import styles from '../../../styles.module.less';
import CollationBiWeekSelector from './CollationBiWeek';
import CollationCycleSelector from './CollationCycleSelector';
import CollationShiftSelector from './CollationShiftSelector';

interface CollationSelectorProps {
  form: FormInstance;
}

/**
 * @description Component to select the collation marking type
 */
const CollationSelector = ({ form }: CollationSelectorProps) => {
  const DEFAULT_BREAK_START_TIME = moment('13:00:00', 'HH:mm:ss');
  const { workJourneyForm, setWorkJourneyForm } = useWorkJourneyContext();
  const { breakStartTime, breakMarkingType } = workJourneyForm;
  const intl = useIntl();

  const handleBreakMarkingTypeChange = (
    value: string,
    breakStartTimeValue: Moment | string | null,
  ) => {
    let resolvedBreakStartTime = breakStartTimeValue;
    if (!resolvedBreakStartTime) resolvedBreakStartTime = DEFAULT_BREAK_START_TIME;
    const breakEndTime = moment(resolvedBreakStartTime).add(1, 'hours');

    setWorkJourneyForm({
      ...workJourneyForm,
      breakMarkingType: value,
      breakStartTime: resolvedBreakStartTime,
      breakEndTime,
    });
  };

  useEffect(() => {
    form.setFieldsValue({ breakStartTime: workJourneyForm.breakStartTime });
  }, [workJourneyForm.breakStartTime, form]);

  const resolvedBreakStartTime =
    breakStartTime && moment.isMoment(breakStartTime) ? breakStartTime : moment(breakStartTime);

  return (
    <>
      <Row className={styles.formSectionRow}>
        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
          <Form.Item name="breakMarkingType" label="" style={{ marginBottom: 0 }}>
            <>
              <div style={{ marginBottom: 16 }}>
                <span className={styles.hint}>
                  {intl.formatMessage({ id: 'workJourney.collation.hint' })}
                </span>
              </div>

              <Radio.Group
                onChange={e => handleBreakMarkingTypeChange(e.target.value, breakStartTime)}
                value={breakMarkingType}
              >
                <Radio.Button value="manual">
                  {intl.formatMessage({ id: 'workJourney.collation.manual' })}
                </Radio.Button>
                <Radio.Button value="automatic">
                  {intl.formatMessage({ id: 'workJourney.collation.automatic' })}
                </Radio.Button>
              </Radio.Group>
            </>
          </Form.Item>
        </Col>
      </Row>

      {workJourneyForm?.type === SHIFT_TYPES.SHIFT_WORK && (
        <Row className={styles.formSectionRow}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <CollationShiftSelector />
          </Col>
        </Row>
      )}
      {workJourneyForm?.type === SHIFT_TYPES.CYCLE && (
        <Row className={styles.formSectionRow}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <CollationCycleSelector />
          </Col>
        </Row>
      )}
      {workJourneyForm?.type === SHIFT_TYPES.BIWEEKLY && (
        <Row className={styles.formSectionRow}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <CollationBiWeekSelector />
          </Col>
        </Row>
      )}
      {(workJourneyForm?.type === SHIFT_TYPES.FIXED ||
        workJourneyForm?.type === SHIFT_TYPES.PART_TIME) && (
        <Row className={styles.formSectionRow}>
          <Col xs={24} sm={12} md={12} lg={12} xl={12}>
            <Form.Item
              name="breakStartTime"
              label={intl.formatMessage({ id: 'workJourney.collation.startTime' })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'workJourney.validation.breakStartTimeRequired' }),
                },
              ]}
            >
              <TimePicker
                format="HH:mm"
                style={{ width: '100%' }}
                value={resolvedBreakStartTime.isValid() ? resolvedBreakStartTime : null}
                onChange={e => {
                  setWorkJourneyForm({ ...workJourneyForm, breakStartTime: e });
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
};

export default CollationSelector;
